import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import CountUp from "react-countup";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  LockKey, ShieldCheck, Check, Warning, CloudArrowUp, FilePdf, FileImage, File as FileIcon,
  ArrowClockwise, SealCheck, Clock, CircleNotch,
} from "@phosphor-icons/react";
import { API, formatApiError } from "../../lib/api";
import { money, dateTime } from "../../lib/format";
import { computeDraw } from "../../lib/calc";
import { MERCHANT_STEPS, docStatusMeta } from "../../lib/stages";
import { StatusChip } from "../../components/StatusChip";
import Countdown from "../../components/Countdown";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";

const mApi = axios.create({ baseURL: API });

export default function MerchantOffer() {
  const { token } = useParams();
  const [status, setStatus] = useState(null); // loading
  const [mToken, setMToken] = useState(sessionStorage.getItem(`offer_${token}`) || "");
  const [offer, setOffer] = useState(null);
  const brandRef = useRef({ company_name: "Business Line OC", accent_color: "#10B981" });

  const loadStatus = useCallback(() => {
    mApi.get(`/offer/${token}/status`).then((r) => {
      setStatus(r.data);
      if (r.data.branding) brandRef.current = r.data.branding;
    }).catch(() => setStatus({ state: "invalid" }));
  }, [token]);

  useEffect(loadStatus, [loadStatus]);

  const loadOffer = useCallback((tok) => {
    mApi.get(`/offer/${token}`, { headers: { Authorization: `Bearer ${tok}` } })
      .then((r) => setOffer(r.data))
      .catch((e) => {
        if (e.response?.status === 401) { sessionStorage.removeItem(`offer_${token}`); setMToken(""); }
        else toast.error(formatApiError(e.response?.data?.detail));
      });
  }, [token]);

  useEffect(() => { if (mToken) loadOffer(mToken); }, [mToken, loadOffer]);

  const accent = brandRef.current.accent_color || "#10B981";

  if (!status)
    return (
      <div className="theme-merchant min-h-screen bg-background flex items-center justify-center">
        <CircleNotch size={30} className="animate-spin text-primary" />
      </div>
    );

  if (["invalid", "revoked", "expired"].includes(status.state))
    return <LockedScreen state={status.state} brand={brandRef.current} />;

  if (status.state === "locked")
    return <LockedScreen state="locked" brand={brandRef.current} lockedUntil={status.locked_until} />;

  if (!mToken || !offer)
    return (
      <PinGate
        token={token}
        brand={brandRef.current}
        businessName={status.business_name}
        onSuccess={(tok) => { sessionStorage.setItem(`offer_${token}`, tok); setMToken(tok); }}
      />
    );

  return <OfferView token={token} mToken={mToken} offer={offer} reload={() => loadOffer(mToken)} accent={accent} brand={brandRef.current} />;
}

/* --------------------------------------------------- PIN GATE */
function PinGate({ token, brand, businessName, onSuccess }) {
  const LEN = 6;
  const [digits, setDigits] = useState(Array(LEN).fill(""));
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const refs = useRef([]);

  const setDigit = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setDigits((p) => { const n = [...p]; n[i] = d; return n; });
    if (d && i < LEN - 1) refs.current[i + 1]?.focus();
  };
  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const onPaste = (e) => {
    const txt = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LEN);
    if (txt) { setDigits(Array.from({ length: LEN }, (_, i) => txt[i] || "")); e.preventDefault(); }
  };

  const submit = async () => {
    const pin = digits.join("").replace(/\s/g, "");
    if (pin.length < 4) return setError("Enter your access code");
    setLoading(true); setError("");
    try {
      const { data } = await mApi.post(`/offer/${token}/verify-pin`, { pin });
      onSuccess(data.merchant_token);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      setDigits(Array(LEN).fill("")); refs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  return (
    <div className="theme-merchant min-h-screen bg-background relative flex items-center justify-center px-4">
      <div className="absolute inset-0 merchant-glow" aria-hidden />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-xl bg-primary/12 border border-primary/25 flex items-center justify-center">
            <ShieldCheck size={20} weight="duotone" className="text-primary" />
          </div>
          <span className="font-display font-bold">{brand.company_name}</span>
        </div>

        <div className="rounded-[var(--radius)] border border-border bg-card shadow-xl p-8">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
            <LockKey size={26} weight="duotone" className="text-primary" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="locked-title">This offer is protected</h1>
          <p className="text-muted-foreground text-sm mt-2">
            {businessName ? <>Prepared for <span className="font-medium text-foreground">{businessName}</span>. </> : null}
            Enter your access code.
          </p>

          <div className="flex justify-center gap-2 mt-7" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (refs.current[i] = el)}
                data-testid={`pin-digit-${i}`}
                inputMode="numeric"
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKey(i, e)}
                className="h-14 w-11 rounded-xl border border-border bg-secondary/50 text-center num text-xl font-semibold focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
              />
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">4–8 digits · 5 failed attempts locks the link.</p>

          {error && <div data-testid="pin-error" className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2 mt-4">{error}</div>}

          <Button onClick={submit} disabled={loading} className="w-full h-11 mt-5 font-semibold" data-testid="pin-submit">
            {loading ? "Unlocking…" : "Unlock Offer"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-5">Trouble accessing? Contact your representative.</p>
      </motion.div>
    </div>
  );
}

/* --------------------------------------------------- LOCKED SCREENS */
function LockedScreen({ state, brand, lockedUntil }) {
  const copy = {
    invalid: { t: "This offer link is not valid", d: "The link may be incorrect. Please contact your representative for a new one." },
    revoked: { t: "This offer link is no longer active", d: "Access has been revoked. Please contact your representative." },
    expired: { t: "This offer has expired", d: "The offer deadline has passed. Please contact your representative to discuss next steps." },
    locked: { t: "Link temporarily locked", d: "Too many incorrect attempts. Please try again in 30 minutes or contact your representative." },
  }[state];
  return (
    <div className="theme-merchant min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-md">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-secondary flex items-center justify-center mb-6">
          {state === "locked" ? <Clock size={30} className="text-muted-foreground" /> : <Warning size={30} className="text-muted-foreground" />}
        </div>
        <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="locked-title">{copy.t}</h1>
        <p className="text-muted-foreground text-sm mt-2">{copy.d}</p>
        <div className="mt-6 text-xs text-muted-foreground">{brand.company_name}</div>
      </motion.div>
    </div>
  );
}

/* --------------------------------------------------- OFFER VIEW */
function currentStepIndex(merchantStage) {
  const idx = MERCHANT_STEPS.findIndex((s) => s.key === merchantStage);
  return idx < 0 ? 0 : idx;
}

function OfferView({ token, mToken, offer, reload, accent, brand }) {
  const p = offer.payment_preview;
  const accepted = offer.acceptance?.accepted;
  const stepIdx = currentStepIndex(offer.merchant_stage);
  const total = offer.documents.length;
  const received = offer.documents.filter((d) => d.status !== "missing").length;
  const hasPricing = offer.terms.pricing_mode === "apr" ? num(offer.terms.apr) : num(offer.terms.factor_rate);
  const showCost = offer.terms.show_tcc !== false;

  return (
    <div className="theme-merchant min-h-screen bg-background text-foreground pb-20">
      {/* Header */}
      <header className="sticky top-0 z-30 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/12 border border-primary/25 flex items-center justify-center">
              <ShieldCheck size={18} weight="duotone" className="text-primary" />
            </div>
            <span className="font-display font-bold text-sm">{brand.company_name}</span>
          </div>
          {offer.offer_expiration && offer.merchant_stage === "offer_sent" && (
            <div className="flex items-center gap-2 text-xs">
              <span className="hidden sm:inline text-muted-foreground">Offer expires in</span>
              <Countdown target={offer.offer_expiration} compact className="text-sm font-semibold" />
            </div>
          )}
        </div>
      </header>

      <div className="relative">
        <div className="absolute inset-x-0 top-0 h-[420px] merchant-glow" aria-hidden />
        <div className="relative max-w-4xl mx-auto px-4 md:px-6">
          {/* HERO */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }} className="pt-14 pb-8 text-center">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/8 px-3 py-1 text-xs font-semibold text-primary">
              <SealCheck size={14} weight="fill" /> You're approved
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-5">{offer.business_name}</h1>
            <p className="text-muted-foreground text-sm mt-1">Business Line of Credit Offer</p>
            <div className="mt-6">
              <div className="overline text-muted-foreground">Approved Credit Limit</div>
              <div className="num font-bold tracking-tighter mt-1" style={{ fontSize: "clamp(3rem, 12vw, 6rem)", lineHeight: 1, color: accent }} data-testid="credit-limit-hero">
                <CountUp end={Number(offer.terms.credit_limit || 0)} duration={1.6} separator="," prefix="$" />
              </div>
            </div>
            {offer.terms_updated_after_acceptance && (
              <div className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-amber-500/15 text-amber-600 px-3 py-1 text-xs font-semibold" data-testid="updated-badge">
                <ArrowClockwise size={13} /> Terms Updated
              </div>
            )}
          </motion.section>

          {/* Countdown banner */}
          {offer.offer_expiration && offer.merchant_stage === "offer_sent" && (
            <div className="rounded-[var(--radius)] border border-border bg-card shadow-sm p-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
              <div><div className="overline text-muted-foreground">Offer Deadline</div><div className="text-sm mt-0.5">{dateTime(offer.offer_expiration)}</div></div>
              <Countdown target={offer.offer_expiration} className="text-xl font-semibold" />
            </div>
          )}

          {/* Status Stepper */}
          <section className="mt-8">
            <div className="rounded-[var(--radius)] border border-border bg-card shadow-sm p-6">
              <div className="overline text-muted-foreground mb-5">Deal Status</div>
              <div className="flex items-center justify-between">
                {MERCHANT_STEPS.map((s, i) => {
                  const done = i < stepIdx;
                  const active = i === stepIdx;
                  return (
                    <div key={s.key} className="flex-1 flex flex-col items-center relative">
                      {i > 0 && <div className="absolute top-4 right-1/2 w-full h-0.5" style={{ background: done || active ? accent : "hsl(var(--border))" }} />}
                      <div className="relative z-10 h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold transition-colors" style={{ background: done ? accent : active ? accent : "hsl(var(--secondary))", color: done || active ? "#fff" : "hsl(var(--muted-foreground))" }} data-testid={`step-${s.key}`}>
                        {done ? <Check size={15} weight="bold" /> : i + 1}
                      </div>
                      <span className={`text-[10px] md:text-xs mt-2 text-center leading-tight ${active ? "font-semibold" : "text-muted-foreground"}`}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Terms grid */}
          <section className="mt-6 grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 rounded-[var(--radius)] border border-border bg-card shadow-sm p-6">
              <div className="overline text-muted-foreground mb-4">Your Terms</div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {hasPricing && <T l="Pricing" v={offer.terms.pricing_mode === "apr" ? `${offer.terms.apr}% APR` : `${offer.terms.factor_rate}x factor rate`} />}
                {offer.terms.payment_frequency && <T l="Payment Frequency" v={offer.terms.payment_frequency} cap />}
                {num(offer.terms.draw_period_months) && <T l="Draw Period" v={`${offer.terms.draw_period_months} months`} />}
                {num(offer.terms.repayment_term_value) && <T l="Repayment Term" v={`${offer.terms.repayment_term_value} ${offer.terms.repayment_term_unit}`} />}
                {num(offer.terms.origination_fee_value) && <T l="Origination Fee" v={offer.terms.origination_fee_type === "pct" ? `${offer.terms.origination_fee_value}%` : money(offer.terms.origination_fee_value)} />}
                {num(offer.terms.draw_fee_pct) && <T l="Draw Fee" v={`${offer.terms.draw_fee_pct}%`} />}
                {num(offer.terms.monthly_maintenance_fee) && <T l="Monthly Maintenance" v={money(offer.terms.monthly_maintenance_fee)} />}
                {num(offer.terms.late_fee) && <T l="Late Fee" v={money(offer.terms.late_fee)} />}
                {num(offer.terms.wire_ach_fee) && <T l="Wire / ACH Fee" v={money(offer.terms.wire_ach_fee)} />}
              </div>
              {offer.terms.prepayment_terms && (
                <div className="mt-5 pt-5 border-t border-border"><T l="Prepayment / Early Payoff" v={offer.terms.prepayment_terms} /></div>
              )}
              {(offer.terms.personal_guarantee || offer.terms.ucc_filing) && (
                <div className="mt-5 pt-5 border-t border-border flex flex-wrap gap-3 text-xs">
                  {offer.terms.personal_guarantee && <Disclosure on label="Personal Guarantee Required" />}
                  {offer.terms.ucc_filing && <Disclosure on label="UCC Filing Applies" />}
                </div>
              )}
              {offer.terms.funding_timeline_note && (
                <p className="mt-4 text-sm text-muted-foreground flex items-center gap-2"><Clock size={15} /> {offer.terms.funding_timeline_note}</p>
              )}
            </div>

            <div className="rounded-[var(--radius)] border shadow-sm p-6" style={{ borderColor: `${accent}40`, background: `${accent}0d` }}>
              <div className="overline mb-2" style={{ color: accent }}>Payment Preview</div>
              <div className="num text-3xl font-bold">{money(p.estimated_payment)}</div>
              <div className="text-xs text-muted-foreground mt-1">per {p.payment_frequency} · {p.number_of_payments} payments</div>
              <div className="mt-5 space-y-2.5 text-sm">
                <PRow l="Total payback" v={money(p.total_payback)} />
                {num(p.origination_fee) && <PRow l="Origination fee" v={money(p.origination_fee)} />}
                {showCost && <><div className="h-px bg-border" /><PRow l="Total cost of capital" v={money(p.total_cost_of_capital)} bold accent={accent} /></>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-4">Estimated on a full draw of your credit limit.</p>
            </div>
          </section>

          {/* Accept module */}
          <AcceptModule token={token} mToken={mToken} offer={offer} reload={reload} accent={accent} />

          {/* Draw Center (funded lines only) */}
          {offer.stage === "funded" && (
            <DrawCenter token={token} mToken={mToken} offer={offer} reload={reload} accent={accent} />
          )}

          {/* Documents */}
          {(accepted || offer.merchant_stage !== "offer_sent") && (
            <section className="mt-8" data-testid="documents-section">
              <div className="flex items-center justify-between mb-4">
                <div><div className="overline text-muted-foreground">Required Documents</div><h2 className="font-display text-2xl font-bold tracking-tight mt-1">Upload Center</h2></div>
                <div className="text-right">
                  <div className="num text-2xl font-bold" style={{ color: accent }}>{received}<span className="text-muted-foreground text-lg">/{total}</span></div>
                  <div className="text-xs text-muted-foreground">received</div>
                </div>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden mb-5">
                <motion.div className="h-full rounded-full" style={{ background: accent }} initial={{ width: 0 }} animate={{ width: total ? `${(received / total) * 100}%` : "0%" }} transition={{ duration: 0.6 }} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {offer.documents.map((doc) => (
                  <DocCard key={doc.id} token={token} mToken={mToken} doc={doc} reload={reload} accent={accent} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------- ACCEPT MODULE */
function AcceptModule({ token, mToken, offer, reload, accent }) {
  const [checked, setChecked] = useState(false);
  const [sig, setSig] = useState("");
  const [loading, setLoading] = useState(false);
  const [justAccepted, setJustAccepted] = useState(false);

  if (offer.acceptance?.accepted) {
    return (
      <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-[var(--radius)] border shadow-sm p-6 text-center" style={{ borderColor: `${accent}40`, background: `${accent}0d` }} data-testid="accepted-banner">
        <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center mb-3" style={{ background: `${accent}1f` }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path className={justAccepted ? "draw-check" : ""} d="M4 12.5l5 5L20 6.5" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="font-display text-xl font-bold">Offer Accepted</h3>
        <p className="text-sm text-muted-foreground mt-1">E-signed by <span className="signature-font text-lg" style={{ color: accent }}>{offer.acceptance.signature_name}</span> · {dateTime(offer.acceptance.accepted_at)}</p>
      </motion.section>
    );
  }

  if (offer.merchant_stage === "expired" || offer.stage === "expired")
    return null;

  const accept = async () => {
    if (!checked || !sig.trim()) return toast.error("Please review, agree, and sign");
    setLoading(true);
    try {
      await mApi.post(`/offer/${token}/accept`, { signature_name: sig.trim() }, { headers: { Authorization: `Bearer ${mToken}` } });
      setJustAccepted(true);
      toast.success("Offer accepted");
      setTimeout(reload, 900);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <section className="mt-8 rounded-[var(--radius)] border border-border bg-card shadow-sm p-6" data-testid="accept-module">
      <div className="overline text-muted-foreground mb-4">Accept Your Offer</div>
      <div className="max-h-40 overflow-y-auto rounded-xl border border-border bg-secondary/40 p-4 text-xs text-muted-foreground leading-relaxed">
        <p>By accepting, you agree to the line of credit terms presented above, including the approved limit of {money(offer.terms.credit_limit)}
          {(offer.terms.pricing_mode === "apr" ? Number(offer.terms.apr) : Number(offer.terms.factor_rate)) > 0
            ? <>, the stated {offer.terms.pricing_mode === "apr" ? `${offer.terms.apr}% APR` : `${offer.terms.factor_rate}x factor rate`}</>
            : null}, all fees, and the
          {offer.terms.personal_guarantee ? " personal guarantee" : " terms"} and {offer.terms.ucc_filing ? "UCC filing " : ""}disclosures.
          This acceptance is a binding acknowledgment of the offer terms and does not obligate a draw. Final funding is subject to document review and final approval.</p>
      </div>
      <label className="flex items-start gap-3 mt-4 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={setChecked} data-testid="accept-checkbox" className="mt-0.5" />
        <span className="text-sm">I have reviewed and agree to the terms above.</span>
      </label>
      <div className="mt-4">
        <label className="overline text-muted-foreground">Type your full name to sign</label>
        <Input value={sig} onChange={(e) => setSig(e.target.value)} placeholder="Full legal name" data-testid="signature-input" className="mt-1.5 h-12 signature-font text-xl bg-secondary/40 border-border" style={{ color: sig ? accent : undefined }} />
      </div>
      <Button onClick={accept} disabled={loading || !checked || !sig.trim()} className="w-full h-12 mt-5 font-semibold text-base" data-testid="accept-offer-button">
        {loading ? "Accepting…" : "Accept Offer"}
      </Button>
    </section>
  );
}

/* --------------------------------------------------- DOC CARD */
function fileIcon(ct) {
  if (ct?.includes("pdf")) return FilePdf;
  if (ct?.includes("image")) return FileImage;
  return FileIcon;
}

function DocCard({ token, mToken, doc, reload, accent }) {
  const dm = docStatusMeta(doc.status);
  const [drag, setDrag] = useState(false);
  const [progress, setProgress] = useState(null);
  const [done, setDone] = useState(false);
  const inputRef = useRef();
  const locked = doc.status === "approved";

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png", "heic", "heif"].includes(ext)) return toast.error("Unsupported type. Use PDF, JPG, PNG or HEIC.");
    if (file.size > 25 * 1024 * 1024) return toast.error("File too large (max 25 MB).");
    setProgress(0);
    const fd = new FormData(); fd.append("file", file);
    try {
      await mApi.post(`/offer/${token}/documents/${doc.id}/upload`, fd, {
        headers: { Authorization: `Bearer ${mToken}`, "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      setDone(true);
      setTimeout(() => { setProgress(null); setDone(false); reload(); }, 900);
    } catch (e) { setProgress(null); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); if (!locked) upload(e.dataTransfer.files?.[0]); };
  const FIcon = doc.files[0] ? fileIcon(doc.files[0].content_type) : CloudArrowUp;

  return (
    <div className={`rounded-xl border bg-card shadow-sm p-4 transition-colors ${drag ? "border-primary" : "border-border"}`} data-testid={`doc-card-${doc.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{doc.name}</span>
            {!doc.required && <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-secondary rounded px-1.5 py-0.5">Optional</span>}
          </div>
          {doc.instruction && <p className="text-xs text-muted-foreground mt-0.5">{doc.instruction}</p>}
        </div>
        <StatusChip label={dm.label} color={dm.color} bg={dm.bg} />
      </div>

      {doc.status === "rejected" && doc.rejection_reason && (
        <div className="mt-3 rounded-lg bg-destructive/10 text-destructive text-xs px-3 py-2 flex items-start gap-1.5">
          <Warning size={14} className="mt-0.5 shrink-0" weight="fill" /> {doc.rejection_reason}
        </div>
      )}

      {doc.files.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {doc.files.map((f) => {
            const FI = fileIcon(f.content_type);
            return (
              <div key={f.id} className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/40 rounded-lg px-2.5 py-1.5">
                <FI size={15} weight="duotone" style={{ color: accent }} />
                <span className="truncate flex-1">{f.original_filename}</span>
                <span className="num">{(f.size / 1024 / 1024).toFixed(1)}MB</span>
              </div>
            );
          })}
        </div>
      )}

      {locked ? (
        <div className="mt-3 flex items-center gap-1.5 text-xs font-medium" style={{ color: accent }}><Check size={14} weight="bold" /> Approved & locked</div>
      ) : progress !== null ? (
        <div className="mt-3">
          {done ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: accent }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path className="draw-check" d="M4 12.5l5 5L20 6.5" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
              Uploaded
            </div>
          ) : (
            <>
              <div className="h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: accent }} /></div>
              <div className="text-xs text-muted-foreground mt-1 num">{progress}%</div>
            </>
          )}
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className="mt-3 rounded-xl border border-dashed border-border hover:border-primary/40 cursor-pointer py-3 flex flex-col items-center gap-1 transition-colors"
          data-testid={`upload-zone-${doc.id}`}
        >
          <CloudArrowUp size={22} weight="duotone" className="text-muted-foreground" />
          <span className="text-xs text-muted-foreground">{doc.status === "rejected" ? "Replace file" : "Drop file or tap to upload"}</span>
          <input ref={inputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.heic,.heif" onChange={(e) => upload(e.target.files?.[0])} data-testid={`file-input-${doc.id}`} />
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------- DRAW CENTER */
const drawStatusMeta = (s) => ({
  requested: { label: "Requested", color: "#FBBF24", bg: "rgba(251,191,36,0.16)" },
  funded: { label: "Funded", color: "#10B981", bg: "rgba(16,185,129,0.16)" },
  declined: { label: "Declined", color: "#F87171", bg: "rgba(248,113,113,0.16)" },
  closed: { label: "Repaid", color: "#94A3B8", bg: "rgba(148,163,184,0.16)" },
}[s] || { label: s, color: "#94A3B8", bg: "rgba(148,163,184,0.14)" });

function DrawCenter({ token, mToken, offer, reload, accent }) {
  const summary = offer.draws_summary || { limit: 0, available: 0, outstanding: 0 };
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const amt = Number(String(amount).replace(/[^0-9.]/g, "")) || 0;
  const preview = computeDraw(offer.terms, amt);
  const over = amt > summary.available;
  const pctUsed = summary.limit ? ((summary.limit - summary.available) / summary.limit) * 100 : 0;

  const submit = async () => {
    if (amt <= 0) return toast.error("Enter a draw amount");
    if (over) return toast.error("Amount exceeds your available credit");
    setLoading(true);
    try {
      await mApi.post(`/offer/${token}/draws`, { amount: amt }, { headers: { Authorization: `Bearer ${mToken}` } });
      toast.success("Draw requested — your team will review and fund it");
      setAmount("");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const draws = (offer.draws || []).slice().reverse();

  return (
    <section className="mt-8" data-testid="draw-center">
      <div className="overline text-muted-foreground">Your Line is Active</div>
      <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Draw Center</h2>

      {/* Available balance */}
      <div className="mt-4 rounded-[var(--radius)] border shadow-sm p-6" style={{ borderColor: `${accent}40`, background: `${accent}0d` }}>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="overline" style={{ color: accent }}>Available to Draw</div>
            <div className="num text-4xl font-bold mt-1" data-testid="available-credit">{money(summary.available)}</div>
            <div className="text-xs text-muted-foreground mt-1">of {money(summary.limit)} limit · {money(summary.outstanding)} outstanding</div>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pctUsed)}%`, background: accent }} />
        </div>
      </div>

      {/* Request form + live preview */}
      {summary.available > 0 && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="rounded-[var(--radius)] border border-border bg-card shadow-sm p-6">
            <div className="overline text-muted-foreground mb-3">Request a Draw</div>
            <label className="text-xs text-muted-foreground">Amount to pull</label>
            <div className="relative mt-1.5">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 num text-lg text-muted-foreground">$</span>
              <Input
                data-testid="draw-amount-input"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="50,000"
                className={`num text-lg h-12 pl-8 bg-secondary/40 border-border ${over ? "border-destructive ring-1 ring-destructive/30" : ""}`}
              />
            </div>
            {over && <p className="text-xs text-destructive mt-1.5">Exceeds available credit of {money(summary.available)}.</p>}
            <Button onClick={submit} disabled={loading || amt <= 0 || over} className="w-full h-11 mt-4 font-semibold" data-testid="request-draw-button">
              {loading ? "Requesting…" : "Request Draw"}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-2 text-center">Your team reviews and funds approved draws.</p>
          </div>

          <div className="rounded-[var(--radius)] border shadow-sm p-6" style={{ borderColor: `${accent}40`, background: `${accent}0d` }}>
            <div className="overline mb-3" style={{ color: accent }}>Draw Preview</div>
            <div className="num text-3xl font-bold">{money(preview.estimated_payment)}</div>
            <div className="text-xs text-muted-foreground mt-1">per {preview.payment_frequency} · {preview.number_of_payments} payments</div>
            <div className="mt-4 space-y-2.5 text-sm">
              <PRow l="Draw amount" v={money(preview.draw_amount)} />
              <PRow l="Draw fee" v={money(preview.draw_fee)} />
              <PRow l="Net funded to you" v={money(preview.net_funded)} bold accent={accent} />
              <div className="h-px bg-border" />
              <PRow l="Total payback" v={money(preview.total_payback)} />
              {offer.terms.show_tcc !== false && <PRow l="Total cost" v={money(preview.total_cost_of_capital)} />}
            </div>
          </div>
        </div>
      )}

      {/* Existing draws */}
      {draws.length > 0 && (
        <div className="mt-4 space-y-3">
          <div className="overline text-muted-foreground">Your Draws</div>
          {draws.map((d) => {
            const meta = drawStatusMeta(d.status);
            const p = d.preview || {};
            return (
              <div key={d.id} className="rounded-xl border border-border bg-card shadow-sm p-4" data-testid={`draw-${d.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="num font-semibold text-lg">{money(d.amount)}</div>
                    <div className="text-xs text-muted-foreground">Requested {dateTime(d.requested_at)}</div>
                  </div>
                  <StatusChip label={meta.label} color={meta.color} bg={meta.bg} />
                </div>
                <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <T l="Net funded" v={money(p.net_funded)} />
                  <T l={`Per ${p.payment_frequency || "period"}`} v={money(p.estimated_payment)} />
                  <T l="Payments" v={p.number_of_payments || "—"} />
                  <T l="Total payback" v={money(p.total_payback)} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}


/* --------------------------------------------------- small bits */
const num = (v) => Number(v) > 0;
const T = ({ l, v, cap }) => (
  <div><div className="text-xs text-muted-foreground">{l}</div><div className={`num font-semibold mt-0.5 ${cap ? "capitalize" : ""}`}>{v}</div></div>
);
const PRow = ({ l, v, bold, accent }) => (
  <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="num font-semibold" style={bold ? { color: accent } : {}}>{v}</span></div>
);
const Disclosure = ({ on, label, off }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 ${on ? "bg-secondary text-foreground" : "bg-secondary/50 text-muted-foreground"}`}>
    <ShieldCheck size={14} weight={on ? "fill" : "regular"} /> {on ? label : off}
  </span>
);
